// Cyclops Value Discovery - Use Cases, Questions, and Value Formulas

// ============================================================================
// USE CASES
// ============================================================================

export type UseCaseId = 'defense' | 'commercial' | 'logistics' | 'research'

export interface UseCase {
  id: UseCaseId
  name: string
  tagline: string
  description: string
  icon: string // Lucide icon name
  color: string // Accent color class
  valueMultipliers: {
    missionCriticality: number // How critical is mission success (1-3)
    gpsRiskExposure: number // How often GPS denial is a factor (1-3)
    fleetValueFactor: number // Asset value multiplier
  }
}

export const useCases: UseCase[] = [
  {
    id: 'defense',
    name: 'Defense & ISR',
    tagline: 'Contested environments, GPS-denied operations',
    description:
      'Intelligence, surveillance, and reconnaissance missions in environments where GPS jamming and spoofing are active threats.',
    icon: 'Shield',
    color: 'text-red-400',
    valueMultipliers: {
      missionCriticality: 3,
      gpsRiskExposure: 3,
      fleetValueFactor: 2.5,
    },
  },
  {
    id: 'commercial',
    name: 'Commercial Mapping',
    tagline: 'Precision agriculture, infrastructure inspection',
    description:
      'High-accuracy mapping and inspection operations where GPS accuracy or availability limits operational capability.',
    icon: 'Map',
    color: 'text-blue-400',
    valueMultipliers: {
      missionCriticality: 1.5,
      gpsRiskExposure: 1.2,
      fleetValueFactor: 1,
    },
  },
  {
    id: 'logistics',
    name: 'Logistics & Delivery',
    tagline: 'Last-mile, medical supply, cargo',
    description:
      'Autonomous delivery operations requiring reliable navigation in urban canyons, near infrastructure, or remote areas.',
    icon: 'Package',
    color: 'text-amber-400',
    valueMultipliers: {
      missionCriticality: 2,
      gpsRiskExposure: 1.5,
      fleetValueFactor: 1.2,
    },
  },
  {
    id: 'research',
    name: 'Research & Development',
    tagline: 'Testing, validation, custom applications',
    description:
      'R&D programs developing new autonomous capabilities or validating navigation systems for future deployment.',
    icon: 'FlaskConical',
    color: 'text-purple-400',
    valueMultipliers: {
      missionCriticality: 1,
      gpsRiskExposure: 1,
      fleetValueFactor: 0.8,
    },
  },
]

// ============================================================================
// MISSION PROFILE
// ============================================================================

export interface MissionProfile {
  fleetSize: number
  monthlyFlightHours: number
  altitudeRequirement: 'standard' | 'high_2km' | 'high_6km'
  cameraRequirement: 'eo' | 'thermal' | 'both'
  operationalAreaKm2: number
}

export const altitudeOptions = [
  { value: 'standard', label: 'Standard (< 500m)', description: 'Low altitude operations' },
  { value: 'high_2km', label: 'High Altitude (2km)', description: 'Extended ceiling operations' },
  { value: 'high_6km', label: 'Very High Altitude (6km)', description: 'Maximum ceiling operations' },
]

export const cameraOptions = [
  { value: 'eo', label: 'EO Only', description: 'Electro-optical camera' },
  { value: 'thermal', label: 'Thermal Only', description: 'FLIR or thermal imaging' },
  { value: 'both', label: 'EO + Thermal', description: 'Dual sensor payload' },
]

// ============================================================================
// CHALLENGE ASSESSMENT
// ============================================================================

export type GpsDenialFrequency = 'never' | 'occasional' | 'frequent' | 'constant'
export type MitigationApproach = 'none' | 'backup_gps' | 'manual' | 'other_vps' | 'mission_abort'

export interface ChallengeAssessment {
  gpsDenialFrequency: GpsDenialFrequency
  missionAbortRate: number // Percentage (0-100)
  costPerFailedMission: number // USD
  currentMitigation: MitigationApproach
  annualMissions: number
}

export const gpsDenialOptions = [
  {
    value: 'never',
    label: 'Never',
    description: 'GPS is always reliable in our operations',
    riskFactor: 0,
  },
  {
    value: 'occasional',
    label: 'Occasional',
    description: 'GPS issues 1-2 times per month',
    riskFactor: 0.3,
  },
  {
    value: 'frequent',
    label: 'Frequent',
    description: 'GPS degradation weekly or in specific areas',
    riskFactor: 0.6,
  },
  {
    value: 'constant',
    label: 'Constant',
    description: 'Operating in actively contested environments',
    riskFactor: 1,
  },
]

export const mitigationOptions = [
  {
    value: 'none',
    label: 'No mitigation',
    description: 'We accept the risk or abort missions',
    effectiveness: 0,
  },
  {
    value: 'backup_gps',
    label: 'Backup GPS',
    description: 'Secondary GPS receivers or multi-constellation',
    effectiveness: 0.2,
  },
  {
    value: 'manual',
    label: 'Manual override',
    description: 'Pilot takes manual control when GPS fails',
    effectiveness: 0.4,
  },
  {
    value: 'other_vps',
    label: 'Other VPS/INS',
    description: 'Using another visual or inertial system',
    effectiveness: 0.5,
  },
  {
    value: 'mission_abort',
    label: 'Mission abort',
    description: 'Abort and return when GPS degrades',
    effectiveness: 0,
  },
]

// ============================================================================
// DISCOVERY STATE
// ============================================================================

export type DiscoveryStep =
  | 'use-case'
  | 'mission-profile'
  | 'challenge'
  | 'value'
  | 'solution'
  | 'summary'

export const discoverySteps: { id: DiscoveryStep; label: string; phase: string }[] = [
  { id: 'use-case', label: 'Use Case', phase: 'Discovery' },
  { id: 'mission-profile', label: 'Mission Profile', phase: 'Discovery' },
  { id: 'challenge', label: 'Challenges', phase: 'Discovery' },
  { id: 'value', label: 'Value Analysis', phase: 'Value' },
  { id: 'solution', label: 'Solution', phase: 'Solution' },
  { id: 'summary', label: 'Proposal', phase: 'Export' },
]

export interface DiscoveryState {
  currentStep: DiscoveryStep
  useCase: UseCaseId | null
  missionProfile: MissionProfile
  challenges: ChallengeAssessment
  customerName: string
  customerCompany: string
  customerEmail: string
}

export const initialDiscoveryState: DiscoveryState = {
  currentStep: 'use-case',
  useCase: null,
  missionProfile: {
    fleetSize: 10,
    monthlyFlightHours: 100,
    altitudeRequirement: 'standard',
    cameraRequirement: 'thermal',
    operationalAreaKm2: 25000,
  },
  challenges: {
    gpsDenialFrequency: 'occasional',
    missionAbortRate: 15,
    costPerFailedMission: 25000,
    currentMitigation: 'none',
    annualMissions: 500,
  },
  customerName: '',
  customerCompany: '',
  customerEmail: '',
}

// ============================================================================
// VALUE MESSAGING
// ============================================================================

export interface ValueMessage {
  id: string
  title: string
  description: string
  icon: string
}

export const valueMessages: Record<UseCaseId, ValueMessage[]> = {
  defense: [
    {
      id: 'contested-ops',
      title: 'Operate in Contested Environments',
      description: 'Continue missions when GPS is jammed or spoofed',
      icon: 'ShieldCheck',
    },
    {
      id: 'mission-assurance',
      title: 'Mission Assurance',
      description: 'Complete critical ISR missions regardless of adversary actions',
      icon: 'Target',
    },
    {
      id: 'asset-protection',
      title: 'Asset Protection',
      description: 'Reduce platform losses from navigation failures',
      icon: 'Shield',
    },
  ],
  commercial: [
    {
      id: 'coverage-expansion',
      title: 'Expand Coverage Areas',
      description: 'Operate in GPS-challenged areas like urban canyons',
      icon: 'MapPin',
    },
    {
      id: 'data-quality',
      title: 'Improve Data Quality',
      description: 'Maintain positioning accuracy when GPS degrades',
      icon: 'Focus',
    },
    {
      id: 'operational-continuity',
      title: 'Operational Continuity',
      description: 'Complete surveys even during GPS outages',
      icon: 'Clock',
    },
  ],
  logistics: [
    {
      id: 'delivery-reliability',
      title: 'Delivery Reliability',
      description: 'Complete deliveries in urban environments',
      icon: 'PackageCheck',
    },
    {
      id: 'route-flexibility',
      title: 'Route Flexibility',
      description: 'Navigate near buildings and infrastructure',
      icon: 'Route',
    },
    {
      id: 'regulatory-compliance',
      title: 'Regulatory Readiness',
      description: 'Meet upcoming BVLOS navigation requirements',
      icon: 'FileCheck',
    },
  ],
  research: [
    {
      id: 'capability-development',
      title: 'Capability Development',
      description: 'Develop and validate GPS-independent navigation',
      icon: 'Lightbulb',
    },
    {
      id: 'testing-flexibility',
      title: 'Testing Flexibility',
      description: 'Test in any environment without GPS constraints',
      icon: 'FlaskConical',
    },
    {
      id: 'future-proofing',
      title: 'Future-Proofing',
      description: 'Build resilience into next-gen platforms',
      icon: 'Rocket',
    },
  ],
}

// ============================================================================
// SOLUTION RECOMMENDATIONS
// ============================================================================

export interface RecommendedSolution {
  licenseId: string
  mapsId: string | null
  supportId: string
  integrationId: string
  rndId: string | null
  reasoning: {
    license: string
    maps: string
    support: string
    integration: string
    rnd: string | null
  }
}

export function getRecommendedLicense(fleetSize: number): string {
  if (fleetSize <= 2) return 'lic-trial'
  if (fleetSize <= 10) return 'lic-10'
  if (fleetSize <= 100) return 'lic-100'
  if (fleetSize <= 500) return 'lic-500'
  return 'lic-1000'
}

export function getRecommendedMaps(operationalAreaKm2: number, licenseIncludedKm2: number): string | null {
  const additionalNeeded = operationalAreaKm2 - licenseIncludedKm2
  if (additionalNeeded <= 0) return null
  if (additionalNeeded <= 25000) return 'map-25k'
  if (additionalNeeded <= 50000) return 'map-50k'
  if (additionalNeeded <= 100000) return 'map-100k'
  if (additionalNeeded <= 250000) return 'map-250k'
  return 'map-1m'
}

export function getRecommendedSupport(useCase: UseCaseId, fleetSize: number): string {
  if (useCase === 'defense') return 'sup-ent'
  if (useCase === 'logistics' && fleetSize > 50) return 'sup-ent'
  if (fleetSize > 100 || useCase === 'logistics') return 'sup-prem'
  return 'sup-std'
}

export function getRecommendedIntegration(
  cameraRequirement: string,
  useCase: UseCaseId
): string {
  // FLIR Boson Plus works out of box
  if (cameraRequirement === 'thermal') return 'int-self'
  // Defense usually wants turnkey
  if (useCase === 'defense') return 'int-key'
  // Commercial/logistics might want support
  if (useCase === 'logistics' || useCase === 'commercial') return 'int-sup'
  return 'int-self'
}

export function getRecommendedRnd(altitudeRequirement: string, useCase: UseCaseId): string | null {
  if (altitudeRequirement === 'high_6km') {
    return useCase === 'defense' ? 'rnd-6k-the' : 'rnd-6k-sup'
  }
  if (altitudeRequirement === 'high_2km') {
    return useCase === 'defense' ? 'rnd-2k-the' : 'rnd-2k-sup'
  }
  return null
}

// License included map coverage
export const licenseMapCoverage: Record<string, number> = {
  'lic-trial': 100,
  'lic-10': 25000,
  'lic-100': 100000,
  'lic-500': 200000,
  'lic-1000': 350000,
}

